<template>
  <div class="page-container deviceType-page">
    <!-- 左侧消防系统列表 -->
    <div class="left-system-panel">
      <div class="panel-header">
        <span class="panel-title">消防系统分类</span>
        <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAddSystem">
          新增系统
        </el-button>
      </div>
      <div class="system-list" v-loading="systemLoading">
        <div
          v-for="item in systemList"
          :key="item.uuid"
          class="system-item"
          :class="{ active: currentSystem && currentSystem.uuid === item.uuid }"
          @click="handleSystemClick(item)"
        >
          <div class="system-item-info">
            <div class="system-item-icon">{{ getSystemIcon(item.systemCode) }}</div>
            <div>
              <div class="system-item-name">{{ item.systemName }}</div>
              <div class="system-item-code">{{ item.systemCode }}</div>
            </div>
          </div>
          <span class="system-item-count">{{ item.deviceTypeCount || 0 }} 种设施</span>
        </div>
        <div v-if="!systemLoading && systemList.length === 0" class="empty-placeholder">
          <i class="el-icon-folder-opened"></i>
          <p>暂无消防系统</p>
        </div>
      </div>
    </div>

    <!-- 右侧设施类型列表 -->
    <div class="right-type-panel">
      <div class="panel-header">
        <span class="panel-title">
          设施类型列表
          <span v-if="currentSystem" style="color: #2296f7;"> - {{ currentSystem.systemName }}</span>
        </span>
        <div class="header-actions" v-if="currentSystem">
          <el-button type="primary" size="mini" icon="el-icon-plus" @click="handleAddType">
            新增设施类型
          </el-button>
          <el-button type="primary" plain size="mini" icon="el-icon-edit" @click="handleEditSystem">
            编辑系统
          </el-button>
          <el-button type="danger" size="mini" icon="el-icon-delete" @click="handleDeleteSystem">
            删除系统
          </el-button>
        </div>
      </div>

      <div class="type-content" v-loading="typeLoading">
        <el-table :data="typeList" border stripe v-if="currentSystem" :header-cell-style="{ textAlign: 'center' }" :cell-style="{ textAlign: 'center' }">
          <el-table-column prop="typeCode" label="编码" width="120">
            <template slot-scope="scope">
              <code style="color: #2296f7;">{{ scope.row.typeCode }}</code>
            </template>
          </el-table-column>
          <el-table-column prop="typeName" label="设施名称" min-width="150"></el-table-column>
          <el-table-column prop="systemName" label="所属系统" width="140"></el-table-column>
          <el-table-column prop="description" label="说明" min-width="200" show-overflow-tooltip></el-table-column>
          <el-table-column label="操作" width="160" fixed="right">
            <template slot-scope="scope">
              <el-button type="primary" plain size="mini" @click="handleEditType(scope.row)">编辑</el-button>
              <el-button type="danger" plain size="mini" @click="handleDeleteType(scope.row)">删除</el-button>
            </template>
          </el-table-column>
        </el-table>
        <div v-else class="empty-placeholder center">
          <i class="el-icon-mouse"></i>
          <p>请在左侧选择消防系统</p>
        </div>
      </div>
    </div>

    <!-- 消防系统弹窗 -->
    <SystemDialog
      :visible.sync="systemDialogVisible"
      :type="systemDialogType"
      :initial-data="systemInitialData"
      @success="handleSystemSuccess"
    />

    <!-- 设施类型弹窗 -->
    <DeviceTypeDialog
      :visible.sync="typeDialogVisible"
      :type="typeDialogType"
      :initial-data="typeInitialData"
      :system-list="systemList"
      :current-system-id="currentSystem ? currentSystem.uuid : ''"
      @success="handleTypeSuccess"
    />
  </div>
</template>

<script>
// 服务
import { deviceTypeService } from './deviceType.service';
// 组件
import SystemDialog from './systemDialog.vue';
import DeviceTypeDialog from './deviceTypeDialog.vue';

export default {
  name: 'DeviceTypeList',
  components: {
    SystemDialog,
    DeviceTypeDialog
  },
  data() {
    return {
      // 消防系统相关
      systemList: [],
      systemLoading: false,
      currentSystem: null,
      systemDialogVisible: false,
      systemDialogType: 'add',
      systemInitialData: {},

      // 设施类型相关
      typeList: [],
      typeLoading: false,
      typeDialogVisible: false,
      typeDialogType: 'add',
      typeInitialData: {},

      // 系统图标映射
      systemIconMap: {
        'ZDBJ': '🔔',
        'ZDPL': '💧',
        'XHS': '🚒',
        'FHFM': '🚪',
        'FHFG': '🚪',
        'YJZM': '💡',
        'DQHZ': '⚡',
        'FPY': '🌬️'
      }
    };
  },
  mounted() {
    this.initData();
  },
  methods: {
    initData() {
      this.loadSystemList();
    },

    getSystemIcon(code) {
      return this.systemIconMap[code] || '🔧';
    },

    // ==================== 消防系统相关方法 ====================
    loadSystemList() {
      this.systemLoading = true;
      deviceTypeService.listFireSystems().then(res => {
        this.systemList = res.data || [];
      }).finally(() => {
        this.systemLoading = false;
      });
    },

    handleSystemClick(item) {
      this.currentSystem = item;
      this.loadTypeList();
    },

    handleAddSystem() {
      this.systemDialogType = 'add';
      this.systemInitialData = {};
      this.systemDialogVisible = true;
    },

    handleEditSystem() {
      if (!this.currentSystem) return;
      this.systemDialogType = 'edit';
      this.systemInitialData = { ...this.currentSystem };
      this.systemDialogVisible = true;
    },

    handleSystemSuccess() {
      this.loadSystemList();
    },

    handleDeleteSystem() {
      if (!this.currentSystem) return;
      if (this.typeList.length > 0) {
        this.$message.warning('该系统下存在设施类型，请先删除设施类型');
        return;
      }
      this.$confirm(`确认删除消防系统"${this.currentSystem.systemName}"吗？`, '提示', { type: 'warning' }).then(() => {
        this.systemLoading = true;
        deviceTypeService.deleteFireSystem(this.currentSystem.uuid).then(() => {
          this.$message.success('删除成功');
          this.currentSystem = null;
          this.typeList = [];
          this.loadSystemList();
        }).finally(() => {
          this.systemLoading = false;
        });
      }).catch(() => {});
    },

    // ==================== 设施类型相关方法 ====================
    loadTypeList() {
      if (!this.currentSystem) return;
      this.typeLoading = true;
      deviceTypeService.listDeviceTypes(this.currentSystem.uuid).then(res => {
        this.typeList = res.data || [];
      }).finally(() => {
        this.typeLoading = false;
      });
    },

    handleAddType() {
      this.typeDialogType = 'add';
      this.typeInitialData = {};
      this.typeDialogVisible = true;
    },

    handleEditType(row) {
      this.typeDialogType = 'edit';
      this.typeInitialData = { ...row };
      this.typeDialogVisible = true;
    },

    handleTypeSuccess() {
      this.loadTypeList();
      this.loadSystemList(); // 刷新设施数量
    },

    handleDeleteType(row) {
      this.$confirm(`确认删除设施类型"${row.typeName}"吗？`, '提示', { type: 'warning' }).then(() => {
        this.typeLoading = true;
        deviceTypeService.deleteDeviceType(row.uuid).then(() => {
          this.$message.success('删除成功');
          this.loadTypeList();
          this.loadSystemList();
        }).finally(() => {
          this.typeLoading = false;
        });
      }).catch(() => {});
    }
  }
};
</script>

<style scoped lang="scss">
.deviceType-page {
  display: flex;
  height: calc(100vh - 125px);
  background: #f5f7fa;
  padding: 15px;
  gap: 15px;
  box-sizing: border-box;

  .left-system-panel {
    width: 400px;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;

    .panel-header {
      padding: 15px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .panel-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }
    }

    .system-list {
      flex: 1;
      overflow: auto;
      padding: 10px;

      .system-item {
        padding: 12px 15px;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        background: #f5f7fa;
        border: 1px solid #e4e7ed;
        transition: all 0.2s;

        &:hover {
          border-color: #2296f7;
        }

        &.active {
          background: #ecf5ff;
          border-color: #2296f7;
        }

        .system-item-info {
          display: flex;
          align-items: center;
          gap: 12px;

          .system-item-icon {
            width: 36px;
            height: 36px;
            border-radius: 8px;
            background: #ecf5ff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 18px;
          }

          .system-item-name {
            font-size: 14px;
            color: #303133;
            font-weight: 500;
          }

          .system-item-code {
            font-size: 12px;
            color: #909399;
            font-family: monospace;
          }
        }

        .system-item-count {
          background: #ecf5ff;
          padding: 4px 10px;
          border-radius: 20px;
          font-size: 12px;
          color: #2296f7;
        }
      }
    }
  }

  .right-type-panel {
    flex: 1;
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;

    .panel-header {
      padding: 15px 20px;
      border-bottom: 1px solid #ebeef5;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .panel-title {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
      }

      .header-actions {
        display: flex;
        gap: 10px;
      }
    }

    .type-content {
      flex: 1;
      padding: 20px;
      overflow: auto;
    }
  }

  .danger-text {
    color: #f56c6c;
  }

  .empty-placeholder {
    padding: 40px 0;
    text-align: center;
    color: #909399;

    &.center {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
    }

    i {
      font-size: 48px;
      margin-bottom: 10px;
      color: #E4E7ED;
    }

    p {
      font-size: 14px;
      margin: 0;
    }
  }
}
</style>

<!-- 全局样式：确保消息提示和确认框不被弹窗遮挡 -->
<style lang="scss">
.el-message {
  z-index: 10000 !important;
}
.el-message-box__wrapper {
  z-index: 10001 !important;
}
</style>
